import React from "react"

import { Box, Grid, IconButton, Stack, Tooltip, Typography, } from "@mui/material"

import { i18n } from "@lingui/core"
import { DateTime } from "luxon"

import {
	mdiAccountOutline,
	mdiCheckboxMarkedCircleOutline,
	mdiClockTimeEightOutline,
	mdiSkipNext,
	mdiTrashCanOutline,
} from "@mdi/js"
import Icon from "@mdi/react"

import { Trans } from "@lingui/macro"
import { Action, ActionStatus, ActionUtils, SessionResponse } from "models"

export interface ActionDetailProps {
	data: Action
	sessionData: SessionResponse
	showButtons?: boolean
	openDeferDialog?: () => void
	openMarkAsDone?: () => void
	openAssignDialog?: () => void
	openSkipDialog?: () => void
	openSendEmailDialog?: () => void
	onClick?: () => void
}
export interface ActionDetailState {
}
export class ActionDetail extends React.Component<ActionDetailProps, ActionDetailState> {
	static defaultProps = {
		showButtons: true,
	}

	private isSmallScreen: boolean = window.innerWidth < 1024

	private printActionType = () => {
		const action = i18n._("enums.workflow-step-types." + this.props.data.actionType)
		if (this.isSmallScreen) {
			return action
		}
		return i18n._({ id: "actions.action-type-label", message: "Tipo di azione:" }) + " " + action
	}

	private printTime = () => {
		let { deadline } = this.props.data
		const dateTime = DateTime.fromJSDate(new Date(deadline))

		const formattedDate = dateTime.toFormat("dd/MM/yyyy")

		if (this.isSmallScreen) {
			return formattedDate
		}
		return i18n._({ id: "actions.deadline-new-label", message: "Scadenza:" }) + " " + formattedDate
	}

	private canExecuteAction = () => {
		const currentAccountId = this.props.sessionData?.accountDetails?.account?.id

		if (!this.props.data.executor)
			return true

		if (this.props.data.executor?.id === currentAccountId)
			return true;

		return false
	}

	render() {
		let iconColor: string = "secondary.main"
		let today = new Date()
		let deadline = new Date(this.props.data.deadline)
		today.setHours(0, 0, 0, 0)
		deadline.setHours(0, 0, 0, 0)


		if (this.props.data.status === ActionStatus.WORKING) {
			iconColor = "primary.main"
		}
		if (deadline.getTime() < today.getTime()) {
			iconColor = "error.main"
		}

		return (
			<Grid container spacing={2} alignItems="center" p={0.5} direction="row">
				<Grid item xs="auto">
					<Box
						component={Icon}
						path={ActionUtils.renderIcon(this.props.data)}
						size="35px"
						sx={{
							color: "primary.contrastText",
							backgroundColor: iconColor,
							padding: "5px",
							mt: 2,
							borderRadius: "100%",
						}}>
					</Box>
				</Grid>
				<Grid item xs onClick={this.props.onClick}>
					<Stack direction="column">
						<Typography sx={{ fontSize: 16, fontWeight: "medium" }} color="primary">
							{this.props.data.name}
						</Typography>
						{this.props.data.workflow?.name && (
							<Typography sx={{ fontSize: 10, fontWeight: "600", color: "secondary.main" }}>
								{this.props.data.workflow.name}
							</Typography>
						)}
						<Typography sx={{ fontSize: 9, fontWeight: '600' }}>
							{this.printActionType()}
						</Typography>
						<Typography sx={{ fontSize: 9 }}>
							<Trans id={"actions.assignedTo"}>
								Assegnata a &nbsp;
								{
									this.props.data.executor ?
										(
											(this.props.data.executor.accountDetails && this.props.data.executor.accountDetails.firstName && this.props.data.executor.accountDetails.lastName) ?
												<span>{this.props.data.executor.accountDetails.firstName} {this.props.data.executor.accountDetails.lastName}</span> :
												<span>{this.props.data.executor.username}</span>
										) :
										<Trans id={"workflow-actions.unassigned"}>
											nessuno
										</Trans>
								}
							</Trans>
						</Typography>
						<Typography sx={{ fontSize: 10 }}>
							{this.printTime()}
						</Typography>
					</Stack>
				</Grid>

				{this.props.showButtons &&
					<Grid item xs="auto">
						<Stack direction="row" spacing={0.7} sx={{ display: "block", }}>
							<Tooltip title="Assegna" placement="top" arrow>
								<IconButton
									sx={{
										color: "grey",
										backgroundColor: "transparent",
										":hover": {
											color: "secondary.main",
										},
									}}
									aria-label="assign"
									onClick={this.props.openAssignDialog}
									disableFocusRipple
									disableRipple>
									<Icon path={mdiAccountOutline} size={1} />
								</IconButton>
							</Tooltip>
							<Tooltip title="Posticipa" placement="top" arrow>
								<IconButton
									sx={{
										color: 'grey',
										backgroundColor: "transparent",
										":hover": {
											color: "secondary.main"
										}
									}}
									aria-label="Postpone"
									onClick={this.props.openDeferDialog}
									disableFocusRipple
									disableRipple>
									<Icon path={mdiClockTimeEightOutline} size={1} />
								</IconButton>
							</Tooltip>
							{
								(this.props.data.status === ActionStatus.WORKING || (deadline.getTime() < today.getTime())) &&
								<>
									{
										this.props.data.workflow &&
										<Tooltip title="Salta" placement="top" arrow>
											<IconButton
												sx={{
													color: 'grey',
													backgroundColor: "transparent",
													":hover": {
														color: "secondary.main"
													}
												}}
												aria-label="skip"
												onClick={this.props.openSkipDialog}
												disableFocusRipple
												disableRipple>
												<Icon path={mdiSkipNext} size={1} />
											</IconButton>
										</Tooltip>
									}
									{
										!this.props.data.workflow &&
										<Tooltip title="Elimina" placement="top" arrow>
											<IconButton
												sx={{
													color: 'grey',
													backgroundColor: "transparent",
													":hover": {
														color: "secondary.main"
													}
												}}
												aria-label="delete"
												onClick={this.props.openSkipDialog}
												disableFocusRipple
												disableRipple>
												<Icon path={mdiTrashCanOutline} size={1} />
											</IconButton>
										</Tooltip>
									}
									{this.canExecuteAction() && <Tooltip title="Eseguita" placement="top" arrow>
										<IconButton
											sx={{
												p: 0,
												color: 'grey',
												backgroundColor: "transparent",
												":hover": {
													color: "secondary.main"
												}
											}}
											aria-label="Mark as done"
											onClick={this.props.openMarkAsDone}
											disableFocusRipple
											disableRipple>
											<Icon path={mdiCheckboxMarkedCircleOutline} size={1} />
										</IconButton>
									</Tooltip>}
								</>

							}
						</Stack>
					</Grid>
				}
			</Grid>
		)
	}
}
